import { useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { getFightingStyleLevel, getASILevels } from '@character-creator/data';
import { COMMONER_HIT_DIE } from '@character-creator/core';
import type { CharacterClass, SkillName, AbilityName, Subclass } from '@character-creator/types';
import { useCharacterCreationStore, type ClassLevel } from '../../../stores/characterCreationStore';
import { useRulesetClasses, useRulesetClass } from '../../../hooks/useRulesetData';
import { formatAbility, formatSkill } from '../../../utils/formatting';
import { DiceIcon } from '../../ui/Icons';
import { FightingStyleSelector } from '../FightingStyleSelector';
import { ExpertiseSelector } from '../ExpertiseSelector';
import { hasExpertise, getExpertiseLevel, getExpertiseAllowed } from '../expertiseUtils';
import { LandCircleSelector } from '../LandCircleSelector';
import { PactBoonSelector } from '../PactBoonSelector';
import { FavoredEnemySelector } from '../FavoredEnemySelector';
import { NaturalExplorerSelector } from '../NaturalExplorerSelector';
import { MetamagicSelector } from '../MetamagicSelector';
import { EldritchInvocationSelector } from '../EldritchInvocationSelector';
import { WildShapeSelector } from '../WildShapeSelector';
import { getFavoredEnemyCount, getNaturalExplorerCount, getMetamagicCount, getInvocationCount, getFeat } from '@character-creator/data';

export function ClassStep() {
  const {
    draft,
    setClass,
    setSkillProficiencyChoices,
    setConfirmedCommoner,
    setClassLevelSkills,
    setSubclass,
    setFightingStyle,
    toggleExpertise,
    setLandCircleTerrain,
    setPactBoon,
    toggleFavoredEnemy,
    toggleNaturalExplorerTerrain,
    toggleMetamagic,
    toggleEldritchInvocation,
    setIdentitySubTab,
    incrementClassLevel,
    decrementClassLevel,
    addClassLevel,
    clearClass,
  } = useCharacterCreationStore();

  const [addingClass, setAddingClass] = useState(false);
  const [pendingClass, setPendingClass] = useState<string | null>(null);
  const [pendingSkills, setPendingSkills] = useState<SkillName[]>([]);

  // Use ruleset-aware data
  const classes = useRulesetClasses();
  const selectedClass = useRulesetClass(draft.classId);

  // Local lookup for callbacks
  const getClass = useCallback(
    (id: string) => classes.find((c) => c.id === id),
    [classes]
  );
  const requiredSkillCount = selectedClass?.skillChoices.count ?? 0;
  const isCommoner = draft.confirmedCommoner && !draft.classId;
  const hasMultipleClasses = draft.classLevels.length > 1;
  // Compute characterLevel from classLevels (source of truth) to avoid persist desync bug
  const characterLevel = draft.classLevels.length > 0
    ? draft.classLevels.reduce((sum, cl) => sum + cl.level, 0)
    : 1;
  const canAddMoreLevels = characterLevel < 20;


  // Check multiclass prerequisites
  const meetsMulticlassRequirements = (classId: string): { meets: boolean; reason?: string } => {
    const cls = getClass(classId);
    if (!cls?.multiclassRequirements) return { meets: true };

    const logic = cls.multiclassRequirementsLogic ?? 'and';
    const checkFn = logic === 'and' ? 'every' : 'some';

    const meets = cls.multiclassRequirements[checkFn](
      (req: { ability: AbilityName; minimum: number }) => {
        const score = draft.abilityScores[req.ability] ?? 0;
        return score >= req.minimum;
      }
    );

    if (!meets) {
      const conjunction = logic === 'and' ? ' and ' : ' or ';
      const reqStrings = cls.multiclassRequirements.map(
        (r: { ability: AbilityName; minimum: number }) =>
          `${formatAbility(r.ability)} ${r.minimum}+`
      );
      return { meets: false, reason: `Requires ${reqStrings.join(conjunction)}` };
    }

    return { meets: true };
  };

  const handleSkillToggle = (skill: SkillName) => {
    const current = draft.skillProficiencyChoices;
    if (current.includes(skill)) {
      setSkillProficiencyChoices(current.filter((s) => s !== skill));
    } else if (current.length < requiredSkillCount) {
      setSkillProficiencyChoices([...current, skill]);
    }
  };

  const handleMulticlassSkillToggle = (classId: string, skill: SkillName) => {
    const classLevel = draft.classLevels.find((cl) => cl.classId === classId);
    if (!classLevel) return;
    const cls = getClass(classId);
    if (!cls) return;

    const current = classLevel.skillProficiencyChoices;
    if (current.includes(skill)) {
      setClassLevelSkills(
        classId,
        current.filter((s) => s !== skill)
      );
    } else if (current.length < cls.skillChoices.count) {
      setClassLevelSkills(classId, [...current, skill]);
    }
  };

  const handlePendingSkillToggle = (skill: SkillName) => {
    if (!pendingClass) return;
    const cls = getClass(pendingClass);
    if (!cls) return;

    if (pendingSkills.includes(skill)) {
      setPendingSkills(pendingSkills.filter((s) => s !== skill));
    } else if (pendingSkills.length < cls.skillChoices.count) {
      setPendingSkills([...pendingSkills, skill]);
    }
  };

  const handleAddClass = (classId: string) => {
    const cls = getClass(classId);
    if (!cls) return;
    setPendingClass(classId);
    setPendingSkills([]);
    setAddingClass(false);
  };

  const confirmAddClass = () => {
    if (!pendingClass) return;
    const cls = getClass(pendingClass);
    if (!cls) return;

    if (pendingSkills.length === cls.skillChoices.count) {
      addClassLevel(pendingClass, pendingSkills);
      setPendingClass(null);
      setPendingSkills([]);
    }
  };

  const cancelAddClass = () => {
    setPendingClass(null);
    setPendingSkills([]);
    setAddingClass(false);
  };

  // Get classes available for multiclassing (not already taken)
  const availableForMulticlass = classes.filter(
    (cls) => !draft.classLevels.some((cl) => cl.classId === cls.id)
  );

  const handleSuggest = () => {
    const randomClass = classes[Math.floor(Math.random() * classes.length)];
    setClass(randomClass.id);
    // Randomly select the required number of skills
    const availableSkills = [...randomClass.skillChoices.from];
    const shuffled = availableSkills.sort(() => Math.random() - 0.5);
    const selectedSkills = shuffled.slice(0, randomClass.skillChoices.count) as SkillName[];
    setSkillProficiencyChoices(selectedSkills);
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-text-primary mb-2">
            Choose Your Class
          </h2>
          <p className="text-text-secondary">
            Your class defines your capabilities in combat, exploration, and social interaction.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(draft.classLevels.length > 0 || draft.confirmedCommoner) && (
            <button
              type="button"
              onClick={clearClass}
              className="flex-shrink-0 px-3 py-1.5 text-sm rounded-lg text-text-muted hover:text-error hover:bg-error/10 transition-colors"
            >
              Clear
            </button>
          )}
          {draft.classLevels.length === 0 && !addingClass && !pendingClass && (
            <button
              type="button"
              onClick={handleSuggest}
              className="flex-shrink-0 px-3 py-1.5 text-sm rounded-lg bg-background-secondary text-text-primary hover:bg-background-elevated transition-colors flex items-center gap-1.5"
            >
              <DiceIcon className="w-4 h-4" />
              Suggest
            </button>
          )}
        </div>
      </div>

      {/* Character Level Display */}
      {draft.classLevels.length > 0 && (
        <div className="mb-6 p-4 bg-background-card rounded-lg border border-border-default">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm text-text-muted">Character Level</span>
              <div className="text-2xl font-display font-bold text-accent-gold">
                {characterLevel}
              </div>
            </div>
            {hasMultipleClasses && (
              <div className="text-right">
                <span className="text-sm text-text-muted">Multiclass</span>
                <div className="text-sm text-text-primary">
                  {draft.classLevels.map((cl) => {
                    const cls = getClass(cl.classId);
                    return `${cls?.name ?? cl.classId} ${cl.level}`;
                  }).join(' / ')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Current Class Levels (Multiclass Management) */}
      {draft.classLevels.length > 0 && !pendingClass && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text-primary mb-3">Your Classes</h3>
          <div className="space-y-3">
            {draft.classLevels.map((classLevel) => (
              <ClassLevelCard
                key={classLevel.classId}
                classLevel={classLevel}
                canIncrement={canAddMoreLevels}
                canDecrement={draft.classLevels.length > 1 || classLevel.level > 1}
                onIncrement={() => incrementClassLevel(classLevel.classId)}
                onDecrement={() => decrementClassLevel(classLevel.classId)}
                onSkillToggle={(skill) => handleMulticlassSkillToggle(classLevel.classId, skill)}
                onSubclassSelect={(subclassId) => setSubclass(classLevel.classId, subclassId)}
                onFightingStyleSelect={(styleId) => setFightingStyle(classLevel.classId, styleId)}
                onExpertiseToggle={(skill) => toggleExpertise(classLevel.classId, skill)}
                onLandCircleTerrainSelect={(terrainId) => setLandCircleTerrain(classLevel.classId, terrainId)}
                onPactBoonSelect={(pactBoonId) => setPactBoon(classLevel.classId, pactBoonId)}
                onFavoredEnemyToggle={(enemyId) => toggleFavoredEnemy(classLevel.classId, enemyId)}
                onNaturalExplorerToggle={(terrainId) => toggleNaturalExplorerTerrain(classLevel.classId, terrainId)}
                onMetamagicToggle={(metamagicId) => toggleMetamagic(classLevel.classId, metamagicId)}
                onInvocationToggle={(invocationId) => toggleEldritchInvocation(classLevel.classId, invocationId)}
                knownCantrips={draft.selectedCantrips || []}
                onNavigateToAbilities={() => setIdentitySubTab('abilities')}
              />
            ))}

            {/* Add Another Class Button */}
            {canAddMoreLevels && !addingClass && (
              <button
                type="button"
                onClick={() => setAddingClass(true)}
                className="w-full p-3 border-2 border-dashed border-border-default rounded-lg text-text-muted hover:border-accent-gold hover:text-accent-gold transition-colors"
              >
                + Add Another Class (Multiclass)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Pending Class Selection (for multiclass) */}
      {pendingClass && (
        <div className="mb-6 p-4 bg-background-card rounded-lg border border-accent-gold">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-accent-gold">
              Adding: {getClass(pendingClass)?.name}
            </h3>
            <button
              type="button"
              onClick={cancelAddClass}
              className="text-sm text-text-muted hover:text-error transition-colors"
            >
              Cancel
            </button>
          </div>
          <PendingClassSkillSelection
            classId={pendingClass}
            selectedSkills={pendingSkills}
            onSkillToggle={handlePendingSkillToggle}
            onConfirm={confirmAddClass}
          />
        </div>
      )}

      {/* Multiclass Class Selection */}
      {addingClass && !pendingClass && (
        <div className="mb-6 p-4 bg-background-card rounded-lg border border-border-default">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary">Choose a Class to Add</h3>
            <button
              type="button"
              onClick={cancelAddClass}
              className="text-sm text-text-muted hover:text-error transition-colors"
            >
              Cancel
            </button>
          </div>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {availableForMulticlass.map((cls) => {
              const reqs = meetsMulticlassRequirements(cls.id);
              return (
                <button
                  key={cls.id}
                  type="button"
                  onClick={() => reqs.meets && handleAddClass(cls.id)}
                  disabled={!reqs.meets}
                  className={clsx(
                    'w-full text-left p-3 rounded-lg border transition-all',
                    reqs.meets
                      ? 'bg-background-elevated border-background-elevated hover:border-accent-gold'
                      : 'bg-background-secondary border-background-secondary opacity-60 cursor-not-allowed'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-text-primary">{cls.name}</span>
                    <span className="text-sm text-text-muted">{cls.hitDie} HD</span>
                  </div>
                  {!reqs.meets && (
                    <p className="text-xs text-error mt-1">{reqs.reason}</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Initial Class Selection (when no class chosen yet) */}
      {draft.classLevels.length === 0 && !addingClass && !pendingClass && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Class Selection */}
          <div>
            <h3 className="text-lg font-semibold text-text-primary mb-4">Classes</h3>
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
              {/* Commoner Option */}
              <CommonerCard isSelected={isCommoner} onClick={() => setConfirmedCommoner(true)} />

              {/* Separator */}
              <div className="flex items-center gap-2 py-2">
                <div className="flex-1 h-px bg-border-default" />
                <span className="text-xs text-text-muted">or choose a class</span>
                <div className="flex-1 h-px bg-border-default" />
              </div>

              {classes.map((cls) => (
                <ClassCard
                  key={cls.id}
                  characterClass={cls}
                  isSelected={draft.classId === cls.id}
                  onClick={() => setClass(cls.id)}
                />
              ))}
            </div>
          </div>

          {/* Class Details */}
          <div>
            {isCommoner ? (
              <CommonerDetails />
            ) : selectedClass ? (
              <ClassDetails
                characterClass={selectedClass}
                selectedSkills={draft.skillProficiencyChoices}
                onSkillToggle={handleSkillToggle}
                selectedSubclassId={draft.classLevels[0]?.subclassId ?? null}
                onSubclassSelect={(subclassId) => {
                  if (draft.classId) {
                    setSubclass(draft.classId, subclassId);
                  }
                }}
              />
            ) : (
              <div className="card p-6 text-center text-text-muted">
                <p>Select a class to see details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Card showing a class level with +/- controls
interface ClassLevelCardProps {
  classLevel: ClassLevel;
  canIncrement: boolean;
  canDecrement: boolean;
  onIncrement: () => void;
  onDecrement: () => void;
  onSkillToggle: (skill: SkillName) => void;
  onSubclassSelect: (subclassId: string | null) => void;
  onFightingStyleSelect: (styleId: string | null) => void;
  onExpertiseToggle: (skill: SkillName) => void;
  onLandCircleTerrainSelect: (terrainId: string | null) => void;
  onPactBoonSelect: (pactBoonId: string | null) => void;
  onFavoredEnemyToggle: (enemyId: string) => void;
  onNaturalExplorerToggle: (terrainId: string) => void;
  onMetamagicToggle: (metamagicId: string) => void;
  onInvocationToggle: (invocationId: string) => void;
  knownCantrips: string[];
  onNavigateToAbilities: () => void;
}

function ClassLevelCard({
  classLevel,
  canIncrement,
  canDecrement,
  onIncrement,
  onDecrement,
  onSkillToggle,
  onSubclassSelect,
  onFightingStyleSelect,
  onExpertiseToggle,
  onLandCircleTerrainSelect,
  onPactBoonSelect,
  onFavoredEnemyToggle,
  onNaturalExplorerToggle,
  onMetamagicToggle,
  onInvocationToggle,
  knownCantrips,
  onNavigateToAbilities,
}: ClassLevelCardProps) {
  const cls = useRulesetClass(classLevel.classId);
  if (!cls) return null;

  const requiredSkills = cls.skillChoices.count;
  const hasEnoughSkills = classLevel.skillProficiencyChoices.length === requiredSkills;
  const hasSubclasses = cls.subclasses && cls.subclasses.length > 0;
  const canSelectSubclass = hasSubclasses && classLevel.level >= cls.subclassLevel;
  const needsSubclass = canSelectSubclass && !classLevel.subclassId;
  const selectedSubclass = hasSubclasses ? cls.subclasses?.find((s: Subclass) => s.id === classLevel.subclassId) : null;

  // Fighting style logic
  const fightingStyleLevel = getFightingStyleLevel(classLevel.classId);
  const hasFightingStyle = fightingStyleLevel !== null;
  const canSelectFightingStyle = hasFightingStyle && classLevel.level >= fightingStyleLevel;
  const needsFightingStyle = canSelectFightingStyle && !classLevel.fightingStyleId;

  // Expertise logic (Rogues and Bards)
  const classHasExpertise = hasExpertise(classLevel.classId);
  const expertiseLevel = getExpertiseLevel(classLevel.classId);
  const canSelectExpertise = classHasExpertise && expertiseLevel !== null && classLevel.level >= expertiseLevel;
  const expertiseAllowed = getExpertiseAllowed(classLevel.classId, classLevel.level);
  const needsExpertise = canSelectExpertise && classLevel.expertiseSkills.length < expertiseAllowed;

  // Circle of the Land terrain selection (Druids with Circle of the Land subclass)
  const isDruid = classLevel.classId === 'druid';
  const isCircleOfTheLand = isDruid && classLevel.subclassId === 'circle-of-the-land';
  const canSelectLandTerrain = isCircleOfTheLand && classLevel.level >= 2;
  const needsLandTerrain = canSelectLandTerrain && !classLevel.landCircleTerrainId;

  // Wild Shape (all Druids at level 2+)
  const canUseWildShape = isDruid && classLevel.level >= 2;

  // Pact Boon selection (Warlocks at level 3)
  const isWarlock = classLevel.classId === 'warlock';
  const canSelectPactBoon = isWarlock && classLevel.level >= 3;
  const needsPactBoon = canSelectPactBoon && !classLevel.pactBoonId;

  // Favored Enemy selection (Rangers at levels 1, 6, 14)
  const isRanger = classLevel.classId === 'ranger';
  const favoredEnemyAllowed = isRanger ? getFavoredEnemyCount(classLevel.level) : 0;
  const needsFavoredEnemy = isRanger && classLevel.favoredEnemies.length < favoredEnemyAllowed;

  // Natural Explorer terrain selection (Rangers at levels 1, 6, 10)
  const naturalExplorerAllowed = isRanger ? getNaturalExplorerCount(classLevel.level) : 0;
  const needsNaturalExplorer = isRanger && classLevel.naturalExplorerTerrains.length < naturalExplorerAllowed;

  // Metamagic selection (Sorcerers at levels 3, 10, 17)
  const isSorcerer = classLevel.classId === 'sorcerer';
  const metamagicAllowed = isSorcerer ? getMetamagicCount(classLevel.level) : 0;
  const canSelectMetamagic = isSorcerer && classLevel.level >= 3;
  const needsMetamagic = canSelectMetamagic && classLevel.metamagicChoices.length < metamagicAllowed;

  // Eldritch Invocations selection (Warlocks at level 2+)
  const invocationAllowed = isWarlock ? getInvocationCount(classLevel.level) : 0;
  const canSelectInvocations = isWarlock && classLevel.level >= 2;
  const needsInvocations = canSelectInvocations && classLevel.eldritchInvocations.length < invocationAllowed;

  // ASI/Feat selection (levels 4, 8, 12, 16, 19 for most classes; Fighter gets 6, 14; Rogue gets 10)
  const asiLevels = getASILevels(classLevel.classId);
  const availableAsiLevels = asiLevels.filter(level => level <= classLevel.level);

  return (
    <div className="p-4 bg-background-card rounded-lg border border-border-default">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-semibold text-text-primary">{cls.name}</h4>
          <p className="text-sm text-text-muted">{cls.hitDie} Hit Die</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onDecrement}
            disabled={!canDecrement}
            className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold transition-colors',
              canDecrement
                ? 'bg-background-elevated text-text-primary hover:bg-error hover:text-white'
                : 'bg-background-secondary text-text-muted cursor-not-allowed'
            )}
          >
            -
          </button>
          <span className="w-8 text-center text-lg font-bold text-accent-gold">
            {classLevel.level}
          </span>
          <button
            type="button"
            onClick={onIncrement}
            disabled={!canIncrement}
            className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold transition-colors',
              canIncrement
                ? 'bg-background-elevated text-text-primary hover:bg-success hover:text-white'
                : 'bg-background-secondary text-text-muted cursor-not-allowed'
            )}
          >
            +
          </button>
        </div>
      </div>

      {/* Skill Selection */}
      <div className="pt-3 border-t border-border-default">
        <h5 className="text-sm font-medium text-text-primary mb-2">
          {hasEnoughSkills ? 'Skills' : `Choose ${requiredSkills} Skills`}
          <span className="text-text-muted font-normal ml-2">
            ({classLevel.skillProficiencyChoices.length}/{requiredSkills})
          </span>
        </h5>
        <div className="grid grid-cols-2 gap-1">
          {cls.skillChoices.from.map((skill: SkillName) => {
            const isSelected = classLevel.skillProficiencyChoices.includes(skill);
            const isDisabled =
              !isSelected && classLevel.skillProficiencyChoices.length >= requiredSkills;

            return (
              <button
                key={skill}
                type="button"
                onClick={() => onSkillToggle(skill)}
                disabled={isDisabled}
                className={clsx(
                  'p-1.5 text-xs rounded border text-left transition-all',
                  isSelected
                    ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
                    : isDisabled
                      ? 'bg-background-secondary border-background-secondary text-text-muted cursor-not-allowed'
                      : 'bg-background-secondary border-background-elevated text-text-primary hover:border-text-muted'
                )}
              >
                {formatSkill(skill)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Fighting Style Selection - show when level requirement is met */}
      {canSelectFightingStyle && (
        <div className="pt-3 border-t border-border-default mt-3">
          <FightingStyleSelector
            classId={classLevel.classId as 'fighter' | 'paladin' | 'ranger'}
            selectedStyleId={classLevel.fightingStyleId}
            onSelect={onFightingStyleSelect}
            compact={!needsFightingStyle}
          />
        </div>
      )}

      {/* Fighting Style Preview - show what's coming */}
      {hasFightingStyle && !canSelectFightingStyle && (
        <div className="pt-3 border-t border-border-default mt-3">
          <p className="text-xs text-text-muted">
            Fighting Style at level {fightingStyleLevel}
          </p>
        </div>
      )}

      {/* Expertise Selection - for Rogues and Bards */}
      {canSelectExpertise && (
        <div className="pt-3 border-t border-border-default mt-3">
          <ExpertiseSelector
            classId={classLevel.classId}
            level={classLevel.level}
            proficientSkills={classLevel.skillProficiencyChoices}
            selectedExpertise={classLevel.expertiseSkills}
            onToggle={onExpertiseToggle}
            compact={!needsExpertise}
          />
        </div>
      )}

      {/* Expertise Preview - show what's coming */}
      {classHasExpertise && !canSelectExpertise && (
        <div className="pt-3 border-t border-border-default mt-3">
          <p className="text-xs text-text-muted">
            Expertise at level {expertiseLevel}
          </p>
        </div>
      )}

      {/* Subclass Selection - show when level requirement is met */}
      {canSelectSubclass && (
        <div className="pt-3 border-t border-border-default mt-3">
          <h5 className="text-sm font-medium text-text-primary mb-2">
            {cls.subclassName || 'Subclass'}
            {needsSubclass && (
              <span className="text-warning ml-2 text-xs">(Required)</span>
            )}
          </h5>
          {needsSubclass ? (
            <div className="grid grid-cols-1 gap-1">
              {cls.subclasses?.map((subclass: Subclass) => (
                <button
                  key={subclass.id}
                  type="button"
                  onClick={() => onSubclassSelect(subclass.id)}
                  className="w-full text-left p-2 text-xs rounded border bg-background-secondary border-background-elevated text-text-primary hover:border-accent-gold transition-all"
                >
                  <span className="font-medium">{subclass.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-xs text-success flex items-center gap-1">
              <span>{selectedSubclass?.name}</span>
              <button
                type="button"
                onClick={() => onSubclassSelect(null)}
                className="ml-2 text-text-muted hover:text-error transition-colors"
              >
                (change)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Circle of the Land Terrain Selection - show when subclass is Circle of the Land */}
      {canSelectLandTerrain && (
        <div className="pt-3 border-t border-border-default mt-3">
          <LandCircleSelector
            druidLevel={classLevel.level}
            selectedTerrainId={classLevel.landCircleTerrainId}
            onSelect={onLandCircleTerrainSelect}
            compact={!needsLandTerrain}
          />
        </div>
      )}

      {/* Wild Shape Forms - show for all Druids at level 2+ */}
      {canUseWildShape && (
        <div className="pt-3 border-t border-border-default mt-3">
          <WildShapeSelector
            druidLevel={classLevel.level}
            compact={true}
          />
        </div>
      )}

      {/* Wild Shape Preview - show what's coming for Druids */}
      {isDruid && !canUseWildShape && (
        <div className="pt-3 border-t border-border-default mt-3">
          <p className="text-xs text-text-muted">
            Wild Shape at level 2
          </p>
        </div>
      )}

      {/* Pact Boon Selection - show for Warlocks at level 3+ */}
      {canSelectPactBoon && (
        <div className="pt-3 border-t border-border-default mt-3">
          <PactBoonSelector
            selectedPactBoonId={classLevel.pactBoonId}
            onSelect={onPactBoonSelect}
            compact={!needsPactBoon}
          />
        </div>
      )}

      {/* Pact Boon Preview - show what's coming for Warlocks */}
      {isWarlock && !canSelectPactBoon && (
        <div className="pt-3 border-t border-border-default mt-3">
          <p className="text-xs text-text-muted">
            Pact Boon at level 3
          </p>
        </div>
      )}

      {/* Favored Enemy Selection - show for Rangers */}
      {isRanger && (
        <div className="pt-3 border-t border-border-default mt-3">
          <FavoredEnemySelector
            rangerLevel={classLevel.level}
            selectedEnemies={classLevel.favoredEnemies}
            onToggle={onFavoredEnemyToggle}
            compact={!needsFavoredEnemy}
          />
        </div>
      )}

      {/* Natural Explorer Selection - show for Rangers */}
      {isRanger && (
        <div className="pt-3 border-t border-border-default mt-3">
          <NaturalExplorerSelector
            rangerLevel={classLevel.level}
            selectedTerrains={classLevel.naturalExplorerTerrains}
            onToggle={onNaturalExplorerToggle}
            compact={!needsNaturalExplorer}
          />
        </div>
      )}

      {/* Metamagic Selection - show for Sorcerers at level 3+ */}
      {isSorcerer && classLevel.level >= 3 && (
        <div className="pt-3 border-t border-border-default mt-3">
          <MetamagicSelector
            sorcererLevel={classLevel.level}
            selectedMetamagic={classLevel.metamagicChoices}
            onToggle={onMetamagicToggle}
            compact={!needsMetamagic}
          />
        </div>
      )}

      {/* Eldritch Invocations Selection - show for Warlocks at level 2+ */}
      {isWarlock && classLevel.level >= 2 && (
        <div className="pt-3 border-t border-border-default mt-3">
          <EldritchInvocationSelector
            warlockLevel={classLevel.level}
            pactBoonId={classLevel.pactBoonId}
            knownCantrips={knownCantrips}
            selectedInvocations={classLevel.eldritchInvocations}
            onToggle={onInvocationToggle}
            compact={!needsInvocations}
          />
        </div>
      )}

      {/* ASI/Feat Status Badges - show for each available ASI level */}
      {availableAsiLevels.map((asiLevel) => {
        const choice = classLevel.asiChoices?.[asiLevel] ?? null;
        const choiceText = choice
          ? choice.type === 'feat'
            ? `Feat: ${getFeat(choice.featId)?.name ?? choice.featId}`
            : choice.increases.map(inc => `+${inc.amount} ${inc.ability.charAt(0).toUpperCase() + inc.ability.slice(1)}`).join(', ')
          : null;

        return (
          <div key={asiLevel} className="pt-3 border-t border-border-default mt-3">
            {choiceText ? (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-success">&#10003; Level {asiLevel} ASI: {choiceText}</span>
                <button
                  type="button"
                  onClick={onNavigateToAbilities}
                  className="text-text-muted hover:text-accent-gold transition-colors"
                >
                  (edit in Abilities)
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-warning">&#9888; Level {asiLevel} ASI: Not yet chosen</span>
                <button
                  type="button"
                  onClick={onNavigateToAbilities}
                  className="text-text-muted hover:text-accent-gold transition-colors"
                >
                  (choose in Abilities)
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* ASI Preview - show what's coming */}
      {asiLevels.length > 0 && availableAsiLevels.length === 0 && (
        <div className="pt-3 border-t border-border-default mt-3">
          <p className="text-xs text-text-muted">
            Ability Score Improvement at level {asiLevels[0]}
          </p>
        </div>
      )}

      {/* Subclass Preview - show what's coming */}
      {hasSubclasses && !canSelectSubclass && (
        <div className="pt-3 border-t border-border-default mt-3">
          <p className="text-xs text-text-muted">
            {cls.subclassName || 'Subclass'} at level {cls.subclassLevel}
          </p>
        </div>
      )}
    </div>
  );
}

// Skill selection for pending multiclass addition
interface PendingClassSkillSelectionProps {
  classId: string;
  selectedSkills: SkillName[];
  onSkillToggle: (skill: SkillName) => void;
  onConfirm: () => void;
}

function PendingClassSkillSelection({
  classId,
  selectedSkills,
  onSkillToggle,
  onConfirm,
}: PendingClassSkillSelectionProps) {
  const cls = useRulesetClass(classId);
  if (!cls) return null;

  const requiredSkills = cls.skillChoices.count;
  const hasEnoughSkills = selectedSkills.length === requiredSkills;

  return (
    <div>
      <p className="text-sm text-text-secondary mb-3">
        Choose {requiredSkills} skills for this class:
      </p>
      <div className="grid grid-cols-2 gap-2 mb-4">
        {cls.skillChoices.from.map((skill: SkillName) => {
          const isSelected = selectedSkills.includes(skill);
          const isDisabled = !isSelected && selectedSkills.length >= requiredSkills;

          return (
            <button
              key={skill}
              type="button"
              onClick={() => onSkillToggle(skill)}
              disabled={isDisabled}
              className={clsx(
                'p-2 text-sm rounded-lg border text-left transition-all',
                isSelected
                  ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
                  : isDisabled
                    ? 'bg-background-secondary border-background-secondary text-text-muted cursor-not-allowed'
                    : 'bg-background-secondary border-background-elevated text-text-primary hover:border-text-muted'
              )}
            >
              {formatSkill(skill)}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onConfirm}
        disabled={!hasEnoughSkills}
        className={clsx(
          'w-full py-2 rounded-lg font-medium transition-colors',
          hasEnoughSkills
            ? 'bg-accent-gold text-background-primary hover:bg-accent-gold/90'
            : 'bg-background-secondary text-text-muted cursor-not-allowed'
        )}
      >
        Add {cls.name} (Level 1)
      </button>
    </div>
  );
}

interface CommonerCardProps {
  isSelected: boolean;
  onClick: () => void;
}

function CommonerCard({ isSelected, onClick }: CommonerCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full text-left p-4 rounded-lg border transition-all duration-200',
        isSelected
          ? 'bg-accent-gold/10 border-accent-gold'
          : 'bg-background-card border-background-elevated hover:border-text-muted'
      )}
    >
      <div className="flex items-center justify-between">
        <span
          className={clsx(
            'font-semibold',
            isSelected ? 'text-accent-gold' : 'text-text-primary'
          )}
        >
          Commoner
        </span>
        <span className="text-sm text-text-muted">{COMMONER_HIT_DIE} HD</span>
      </div>
      <p className="text-sm text-text-secondary mt-1 line-clamp-2">
        A simple person without adventuring training. No class features or proficiencies.
      </p>
    </button>
  );
}

function CommonerDetails() {
  return (
    <div className="card p-6 space-y-6">
      <div>
        <h3 className="font-display text-xl font-bold text-accent-gold mb-2">Commoner</h3>
        <p className="text-text-secondary">
          A commoner is a regular person without specialized training. While they lack the
          abilities of an adventurer, they can still have interesting backgrounds and stories.
        </p>
      </div>

      {/* Hit Die */}
      <div className="stat-block text-center p-4">
        <div className="text-2xl font-display font-bold text-accent-gold">
          {COMMONER_HIT_DIE}
        </div>
        <div className="text-xs text-text-muted mt-1">Hit Die</div>
      </div>

      {/* No Proficiencies */}
      <div className="space-y-3 text-sm">
        <div>
          <span className="text-text-muted">Saving Throws:</span>
          <span className="ml-2 text-text-muted italic">None</span>
        </div>
        <div>
          <span className="text-text-muted">Armor:</span>
          <span className="ml-2 text-text-muted italic">None</span>
        </div>
        <div>
          <span className="text-text-muted">Weapons:</span>
          <span className="ml-2 text-text-muted italic">None</span>
        </div>
        <div>
          <span className="text-text-muted">Skills:</span>
          <span className="ml-2 text-text-muted italic">None (from class)</span>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-3 bg-background-secondary rounded-lg">
        <p className="text-sm text-text-secondary">
          As a commoner, you'll rely on your race and background for proficiencies and abilities.
          Your hit points will be 4 + Constitution modifier at level 1.
        </p>
      </div>
    </div>
  );
}

interface ClassCardProps {
  characterClass: CharacterClass;
  isSelected: boolean;
  onClick: () => void;
}

function ClassCard({ characterClass, isSelected, onClick }: ClassCardProps) {
  const hitDieNum = characterClass.hitDie.replace('d', '');

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full text-left p-4 rounded-lg border transition-all duration-200',
        isSelected
          ? 'bg-accent-gold/10 border-accent-gold'
          : 'bg-background-card border-background-elevated hover:border-text-muted'
      )}
    >
      <div className="flex items-center justify-between">
        <span className={clsx(
          'font-semibold',
          isSelected ? 'text-accent-gold' : 'text-text-primary'
        )}>
          {characterClass.name}
        </span>
        <span className="text-sm text-text-muted">
          d{hitDieNum} HD
        </span>
      </div>
      <p className="text-sm text-text-secondary mt-1 line-clamp-2">
        {characterClass.description}
      </p>
    </button>
  );
}

interface ClassDetailsProps {
  characterClass: CharacterClass;
  selectedSkills: SkillName[];
  onSkillToggle: (skill: SkillName) => void;
  selectedSubclassId: string | null;
  onSubclassSelect: (subclassId: string | null) => void;
  classLevel?: number; // Current level in this class (defaults to 1 for initial selection)
}

function ClassDetails({ characterClass, selectedSkills, onSkillToggle, selectedSubclassId, onSubclassSelect, classLevel = 1 }: ClassDetailsProps) {
  const hitDieNum = characterClass.hitDie.replace('d', '');
  const hasSubclasses = characterClass.subclasses && characterClass.subclasses.length > 0;
  const canSelectSubclass = hasSubclasses && classLevel >= characterClass.subclassLevel;
  const selectedSubclass = hasSubclasses
    ? characterClass.subclasses?.find(s => s.id === selectedSubclassId)
    : null;

  return (
    <div className="card p-6 space-y-6">
      <div>
        <h3 className="font-display text-xl font-bold text-accent-gold mb-2">
          {characterClass.name}
        </h3>
        <p className="text-text-secondary">{characterClass.description}</p>
      </div>

      {/* Hit Die & Primary Ability */}
      <div className="grid grid-cols-2 gap-4">
        <div className="stat-block text-center">
          <div className="text-2xl font-display font-bold text-accent-gold">d{hitDieNum}</div>
          <div className="text-xs text-text-muted mt-1">Hit Die</div>
        </div>
        <div className="stat-block text-center">
          <div className="text-lg font-display font-bold text-text-primary">
            {characterClass.primaryAbility.map(a => a.slice(0, 3).toUpperCase()).join(' / ')}
          </div>
          <div className="text-xs text-text-muted mt-1">Primary Ability</div>
        </div>
      </div>

      {/* Proficiencies */}
      <div className="space-y-3 text-sm">
        <div>
          <span className="text-text-muted">Saving Throws:</span>
          <span className="ml-2 text-text-primary">
            {characterClass.savingThrows.map(s => formatAbility(s)).join(', ')}
          </span>
        </div>
        <div>
          <span className="text-text-muted">Armor:</span>
          <span className="ml-2 text-text-primary">
            {characterClass.armorProficiencies.length > 0
              ? characterClass.armorProficiencies.map(a => formatAbility(a)).join(', ')
              : 'None'}
          </span>
        </div>
        <div>
          <span className="text-text-muted">Weapons:</span>
          <span className="ml-2 text-text-primary">
            {characterClass.weaponProficiencies.map(w =>
              typeof w === 'string' && (w === 'simple' || w === 'martial')
                ? `${formatAbility(w)} weapons`
                : w
            ).join(', ')}
          </span>
        </div>
      </div>

      {/* Spellcasting */}
      {characterClass.spellcasting && (
        <div className="p-3 bg-background-secondary rounded-lg">
          <span className="text-sm text-accent-gold font-medium">Spellcaster</span>
          <span className="text-sm text-text-muted ml-2">
            ({formatAbility(characterClass.spellcasting.ability)}-based)
          </span>
        </div>
      )}

      {/* Skill Selection */}
      <div>
        <h4 className="text-sm font-semibold text-text-primary mb-2">
          Choose {characterClass.skillChoices.count} Skills
          <span className="text-text-muted font-normal ml-2">
            ({selectedSkills.length}/{characterClass.skillChoices.count} selected)
          </span>
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {characterClass.skillChoices.from.map((skill) => {
            const isSelected = selectedSkills.includes(skill);
            const isDisabled = !isSelected && selectedSkills.length >= characterClass.skillChoices.count;

            return (
              <button
                key={skill}
                type="button"
                onClick={() => onSkillToggle(skill)}
                disabled={isDisabled}
                className={clsx(
                  'p-2 text-sm rounded-lg border text-left transition-all duration-200',
                  isSelected
                    ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
                    : isDisabled
                      ? 'bg-background-secondary border-background-elevated text-text-muted cursor-not-allowed'
                      : 'bg-background-secondary border-background-elevated text-text-primary hover:border-text-muted'
                )}
              >
                {formatSkill(skill)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Subclass Selection - only show when character level meets requirement */}
      {canSelectSubclass && (
        <div>
          <h4 className="text-sm font-semibold text-text-primary mb-2">
            {characterClass.subclassName || 'Subclass'}
          </h4>
          <div className="space-y-2">
            {characterClass.subclasses?.map((subclass) => (
              <button
                key={subclass.id}
                type="button"
                onClick={() => onSubclassSelect(subclass.id)}
                className={clsx(
                  'w-full text-left p-3 rounded-lg border transition-all duration-200',
                  selectedSubclassId === subclass.id
                    ? 'bg-accent-gold/10 border-accent-gold'
                    : 'bg-background-secondary border-background-elevated hover:border-text-muted'
                )}
              >
                <div className="font-medium text-text-primary">{subclass.name}</div>
                <p className="text-xs text-text-secondary mt-1 line-clamp-2">{subclass.description}</p>
              </button>
            ))}
          </div>
          {selectedSubclass && selectedSubclass.features && selectedSubclass.features.length > 0 && (
            <div className="mt-3 p-3 bg-background-secondary rounded-lg">
              <h5 className="text-xs font-semibold text-accent-gold mb-2">
                {selectedSubclass.name} Features (Level {characterClass.subclassLevel})
              </h5>
              <ul className="space-y-1">
                {selectedSubclass.features
                  .filter(f => f.level === characterClass.subclassLevel)
                  .map((feature) => (
                    <li key={feature.id} className="text-xs text-text-secondary">
                      <span className="font-medium text-text-primary">{feature.name}:</span>{' '}
                      {feature.description.slice(0, 100)}...
                    </li>
                  ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Subclass Preview - show what's coming for classes that get subclass later */}
      {hasSubclasses && !canSelectSubclass && (
        <div className="p-3 bg-background-secondary rounded-lg">
          <p className="text-sm text-text-muted">
            <span className="font-medium text-text-primary">{characterClass.subclassName || 'Subclass'}</span>
            {' '}available at level {characterClass.subclassLevel}
          </p>
        </div>
      )}

      {/* Level 1 Features */}
      <div>
        <h4 className="text-sm font-semibold text-text-primary mb-2">Level 1 Features</h4>
        <ul className="space-y-2">
          {characterClass.features
            .filter(f => f.level === 1)
            .map((feature) => (
              <li key={feature.id} className="text-sm">
                <span className="font-medium text-text-primary">{feature.name}.</span>{' '}
                <span className="text-text-secondary line-clamp-2">{feature.description}</span>
              </li>
            ))}
        </ul>
      </div>
    </div>
  );
}


